import { FaceLandmarker, FilesetResolver, type FaceLandmarkerResult } from '@mediapipe/tasks-vision';
import { LANDMARK_COUNT } from './landmarks';
import type { Delegate, TrackerInitOptions } from './protocol';

/**
 * Creates a MediaPipe FaceLandmarker. Shared by the worker and the main-thread
 * fallback so both paths are configured identically.
 *
 * GPU delegate first (WebGL2 in an OffscreenCanvas inside the worker); if it
 * fails (old Safari, blocked WebGL, headless CI) we retry on CPU (XNNPACK
 * wasm SIMD). The caller learns which delegate is active.
 */
export async function createFaceLandmarker(opts: TrackerInitOptions): Promise<{ landmarker: FaceLandmarker; delegate: Delegate }> {
  // `WasmFileset` is not exported by the package typings; it is the object `forVisionTasks` returns.
  const vision: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>> =
    opts.wasmLoaderPath && opts.wasmBinaryPath
      ? { wasmLoaderPath: opts.wasmLoaderPath, wasmBinaryPath: opts.wasmBinaryPath }
      : await FilesetResolver.forVisionTasks(opts.wasmBasePath, opts.useModule);
  const attempts: Delegate[] = opts.delegate === 'GPU' ? ['GPU', 'CPU'] : ['CPU'];
  let lastError: unknown = null;
  for (const delegate of attempts) {
    try {
      const landmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: opts.modelAssetPath, delegate },
        runningMode: 'VIDEO',
        numFaces: opts.numFaces,
        minFaceDetectionConfidence: opts.minFaceDetectionConfidence,
        minFacePresenceConfidence: opts.minFacePresenceConfidence,
        minTrackingConfidence: opts.minTrackingConfidence,
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: true,
      });
      return { landmarker, delegate };
    } catch (err) {
      lastError = err;
      console.warn(`[landmarker] delegate ${delegate} failed:`, err);
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

export interface PackedFace {
  landmarks: Float32Array;
  matrix: Float32Array | null;
  faceCount: number;
}

/**
 * Picks the largest face (by landmark bounding-box area) and packs it into
 * transferable buffers. Returns null when no face is present.
 */
export function packLargestFace(result: FaceLandmarkerResult): PackedFace | null {
  const faces = result.faceLandmarks;
  if (!faces || faces.length === 0) return null;

  let best = 0;
  if (faces.length > 1) {
    let bestArea = -1;
    for (let i = 0; i < faces.length; i++) {
      const lm = faces[i]!;
      let minX = 1, minY = 1, maxX = 0, maxY = 0;
      for (const p of lm) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
      const area = (maxX - minX) * (maxY - minY);
      if (area > bestArea) {
        bestArea = area;
        best = i;
      }
    }
  }

  const lm = faces[best]!;
  const n = Math.min(lm.length, LANDMARK_COUNT);
  const landmarks = new Float32Array(LANDMARK_COUNT * 3);
  for (let i = 0; i < n; i++) {
    const p = lm[i]!;
    landmarks[i * 3] = p.x;
    landmarks[i * 3 + 1] = p.y;
    landmarks[i * 3 + 2] = p.z;
  }

  let matrix: Float32Array | null = null;
  const m = result.facialTransformationMatrixes?.[best];
  if (m && m.data.length === 16) matrix = Float32Array.from(m.data);

  return { landmarks, matrix, faceCount: faces.length };
}
